import {
  alpha,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { SupportPolicy } from '@provision/entity-types';
import { Editor } from '@provision/rich-text-editor';
import { QueryBuilderDnD } from '@react-querybuilder/dnd';
import { QueryBuilderMaterial } from '@react-querybuilder/material';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useId, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FullField, QueryBuilder, RuleGroupType } from 'react-querybuilder';
import 'react-querybuilder/dist/query-builder.css';
import { uniqBy } from 'remeda';
import { RichTextField } from 'src/components/Forms/RichTextField/rich-text-field';
import { useSelectedPartner, useUserGroupOptions } from 'src/graphql';
import { useSearchAddresses } from 'src/graphql/entities/search/search-address';
import { useSupportPolicyConditionFields } from 'src/graphql/entities/support-policy/hooks/use-support-policy-condition-fields';
import {
  createSupportPolicyDefaultRule,
  deserializeSupportPolicyConditions,
  mapSupportPolicyRuleValues,
  SUPPORT_POLICY_CONDITION_FIELDS,
  SupportPolicyRule,
  SupportPolicyRuleGroup,
  useSupportPolicyConditionOptions,
} from 'src/graphql/entities/support-policy/support-policy.conditions';
import { SUPPORT_POLICY_CONDITION_OPERATORS } from 'src/graphql/entities/support-policy/support-policy.operators';
import {
  NormalizedSupportPolicy,
  normalizeSupportPolicyScopeFields,
} from 'src/graphql/entities/support-policy/support-policy.utils';
import {
  conditionsCustomControls,
  muiComponents,
} from 'src/pages/Settings/rules/rules-form-config';
import {
  QueryOptionsValue,
  SupportedActionQueries,
} from 'src/pages/Settings/rules/use-rules-engine';
import { getValidConditionsCount } from 'src/utils/query-builder-conditions';

import {
  POLICY_CATEGORIES_STANDARD,
  POLICY_TYPE_OPTIONS,
  TYPE_FRIENDLY_NAME_POLICIES_MAP,
} from './policy-constants';
import { getSeverityColors, getSeverityForType } from './policy-severity';

const ConditionsContainer = styled(Box)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'transparent',
  padding: theme.spacing(1.5),

  '& .ruleGroup': {
    flexDirection: 'column-reverse',
    border: 'none',
    background: 'none',
  },

  '& .ruleGroup-body': {
    display: 'grid !important',
    gridTemplateColumns: 'min-content 1fr',
    alignItems: 'start',
    gap: theme.spacing(1.25),

    '& .queryBuilder-dragHandle': {
      display: 'flex',
      alignItems: 'center',
    },
    '& > .rule, & > .ruleGroup': {
      gridColumn: '1 / -1',
    },
  },

  '& .betweenRules, & .betweenRules .MuiInputBase-formControl': {
    minWidth: '5rem',
    maxWidth: '22% !important',
    gridColumn: '1 / -1',
  },

  '& .rule': {
    width: '100%',
    display: 'grid',
    gridTemplateAreas: '"drag field operator value remove"',
    gridTemplateColumns:
      'auto minmax(12rem, 1fr) minmax(8rem, 0.75fr) minmax(16rem, 1.25fr) auto',
    alignItems: 'center',
    gap: theme.spacing(1),
  },

  '& .rule-operators, & .rule-value': {
    minWidth: 0,
  },

  '& .rule-value .MuiFormControl-root, & .rule-value .MuiStack-root': {
    width: '100%',
  },

  '& .queryBuilder-dragHandle': {
    gridArea: 'drag',
  },
  '& .rule-fields': {
    gridArea: 'field',
    minWidth: 0,
  },
  '& .rule-operators': {
    gridArea: 'operator',
  },
  '& .rule-value': {
    gridArea: 'value',
  },
  '& .rule-remove': {
    gridArea: 'remove',
    justifySelf: 'end',
  },

  [theme.breakpoints.down('md')]: {
    '& .ruleGroup-body': {
      gridTemplateColumns: '1fr',
    },
    '& .rule': {
      gridTemplateAreas: `"drag field field field remove"
        "drag operator value value remove"`,
      gridTemplateColumns: 'auto 1fr 1fr 1fr auto',
    },
    '& .betweenRules': {
      gridColumn: '1 / -1',
    },
  },

  [theme.breakpoints.down('sm')]: {
    '& .rule': {
      gridTemplateAreas: `"drag"
        "field"
        "operator"
        "value"
        "remove"`,
      gridTemplateColumns: '1fr',
      alignItems: 'stretch',
    },
    '& .rule-remove': {
      justifySelf: 'end',
      alignSelf: 'flex-start',
    },
    '& .queryBuilder-dragHandle': {
      alignSelf: 'flex-start',
      justifySelf: 'start',
    },
    '& .rule-fields, & .rule-operators, & .rule-value': {
      width: '100%',
    },
    '& .rule-fields .MuiAutocomplete-root, & .rule-operators .MuiFormControl-root': {
      width: '100%',
      minWidth: '0 !important',
    },
    '& .rule-fields .MuiInputBase-root': {
      width: '100%',
    },
    '& .betweenRules, & .betweenRules .MuiInputBase-formControl': {
      maxWidth: '100% !important',
      width: '100%',
    },
  },
}));

const normalizeRuleValuesToStrings = (value: SupportPolicyRule['value']): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'object' && item !== null) {
          return (
            (item as { value?: unknown }).value ??
            (item as { id?: unknown }).id ??
            (item as { label?: unknown }).label ??
            null
          );
        }
        return item;
      })
      .filter((val): val is string | number | boolean => val !== null && val !== undefined)
      .map((val) => String(val));
  }

  if (typeof value === 'object' && value !== null) {
    const nested =
      (value as { value?: unknown }).value ??
      (value as { id?: unknown }).id ??
      (value as { label?: unknown }).label;
    return nested === undefined || nested === null ? [] : [String(nested)];
  }

  return value === undefined || value === null ? [] : [String(value)];
};

const getRuleAttribute = (rule: SupportPolicyRule): string => {
  const field = rule.field as { attribute?: unknown; value?: unknown; uiType?: unknown };

  return typeof field === 'string'
    ? field
    : ((field?.attribute as string) ?? field?.value ?? '');
};

const collectValuesForAttribute = (
  group: SupportPolicyRuleGroup,
  attribute: string,
): string[] => {
  const results: string[] = [];

  const walk = (current?: SupportPolicyRuleGroup) => {
    current?.rules?.forEach((rule) => {
      if ('rules' in rule) {
        walk(rule as SupportPolicyRuleGroup);
        return;
      }

      if (getRuleAttribute(rule) !== attribute) return;

      normalizeRuleValuesToStrings(rule.value).forEach((value) => results.push(value));
    });
  };

  walk(group);

  return results;
};

export interface SupportPolicyFormValues {
  title: string;
  description: string;
  type: string;
  category: string;
  validUntil?: Dayjs | null;
  pinnedAt: boolean;
  userGroups?: string[];
  addressIds: string[];
  clientIds: string[];
  clientToAddressIds: string[];
  enableConditions: boolean;
  conditions: SupportPolicyRuleGroup;
  urgencyLevel?: string | null;
  escalationRules?: string | null;
}

interface SupportPolicyFormProps {
  policy?: Partial<SupportPolicy> | Partial<NormalizedSupportPolicy>;
  onSave?: (values: SupportPolicyFormValues) => void;
  formId?: string;
}

export const SupportPolicyForm = ({ policy, onSave, formId }: SupportPolicyFormProps) => {
  const { userGroupOptions } = useUserGroupOptions();
  const [editorKey, setEditorKey] = useState(0);
  const generatedFormId = useId();

  const {
    fields: conditionFieldDefinitions,
    fieldMap: conditionFieldMap,
    isLoading: isLoadingConditionFields,
  } = useSupportPolicyConditionFields();

  const defaults = useMemo<SupportPolicyFormValues>(() => {
    const scope = normalizeSupportPolicyScopeFields(policy);

    return {
      title: policy?.title ?? '',
      description: policy?.description ?? '',
      type: policy?.type ?? '',
      category: policy?.category ?? '',
      validUntil: policy?.validUntil ? dayjs(policy.validUntil) : null,
      pinnedAt: Boolean(policy?.pinnedAt),
      userGroups: policy?.userGroups?.map((g) => g.id) ?? [],
      addressIds: scope.addressIds,
      clientIds: scope.clientIds,
      clientToAddressIds: scope.clientToAddressIds,
      enableConditions: Boolean(policy?.conditions),
      conditions: deserializeSupportPolicyConditions(policy?.conditions, conditionFieldMap),
      urgencyLevel: policy?.urgencyLevel ?? null,
      escalationRules: policy?.escalationRules ?? null,
    };
  }, [policy, conditionFieldMap]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
    setError,
    clearErrors,
  } = useForm<SupportPolicyFormValues>({
    defaultValues: defaults,
  });

  const { selectedPartner } = useSelectedPartner();
  const conditionsEnabled = watch('enableConditions');
  const watchedConditions = watch('conditions');
  const baseConditionQueryOptions = useSupportPolicyConditionOptions();
  const addressIdsFromConditions = useMemo(
    () => uniqBy(collectValuesForAttribute(watchedConditions, 'addressId'), (id) => id),
    [watchedConditions],
  );

  const { records: addressLookupRecords, isLoading: isAddressLookupLoading } =
    useSearchAddresses({
      searchTerm: null,
      filter: addressIdsFromConditions.length
        ? [
            {
              attribute: 'addressId',
              operator: 'in',
              value: addressIdsFromConditions.join(','),
            },
          ]
        : [],
      limit: addressIdsFromConditions.length || 10,
      showResultsWhenEmptySearchTerm: true,
      skip: addressIdsFromConditions.length === 0,
    });

  const addressLookupOptions = useMemo(
    () =>
      addressLookupRecords?.map((address) => ({
        id: address.id ?? address.addressId,
        value: address.id ?? address.addressId,
        label: address.name ?? address.fullAddress ?? address.address1 ?? address.addressId,
      })) ?? [],
    [addressLookupRecords],
  );

  const conditionQueryOptions = useMemo(() => {
    const existingAddressOptions: QueryOptionsValue = baseConditionQueryOptions[
      SupportedActionQueries.getAddress
    ] ?? {
      options: [],
    };

    const mergedAddressOptions = uniqBy(
      [
        ...(existingAddressOptions.historicalOptions ?? []),
        ...(existingAddressOptions.options ?? []),
        ...addressLookupOptions,
      ],
      (opt) =>
        String(
          (opt as { value?: unknown; id?: unknown }).value ??
            (opt as { value?: unknown; id?: unknown }).id ??
            '',
        ),
    );

    return {
      ...baseConditionQueryOptions,
      [SupportedActionQueries.getAddress]: {
        ...existingAddressOptions,
        options: mergedAddressOptions,
        historicalOptions: mergedAddressOptions,
        isLoading: existingAddressOptions?.isLoading || isAddressLookupLoading,
      },
    };
  }, [addressLookupOptions, baseConditionQueryOptions, isAddressLookupLoading]);
  const queryBuilderContext = useMemo(
    () => ({
      timezone: selectedPartner?.timeZone ?? 'America/New_York',
      queryOptions: conditionQueryOptions,
    }),
    [conditionQueryOptions, selectedPartner?.timeZone],
  );

  const conditionFields = useMemo(
    () =>
      conditionFieldDefinitions.map((field) => {
        if (!field.optionsQuery) {
          return field;
        }

        if (!(field.optionsQuery in conditionQueryOptions)) {
          return field;
        }

        const optionsData =
          conditionQueryOptions[field.optionsQuery as SupportedActionQueries];
        if (!optionsData) {
          return field;
        }

        const options = uniqBy(
          [...(optionsData.historicalOptions ?? []), ...(optionsData.options ?? [])],
          (opt) =>
            String(
              (opt as { id?: string | number | boolean }).id ??
                (opt as { value?: string | number | boolean }).value ??
                (opt as { label?: string }).label ??
                '',
            ),
        );

        return {
          ...field,
          options,
          fieldOptions: options,
        };
      }),
    [conditionFieldDefinitions, conditionQueryOptions],
  );

  const mappedConditions = useMemo(
    () => mapSupportPolicyRuleValues(watchedConditions, conditionQueryOptions),
    [conditionQueryOptions, watchedConditions],
  );

  useEffect(() => {
    if (!conditionsEnabled) return;

    const hasRules = Boolean(watchedConditions?.rules?.length);

    if (!hasRules) {
      setValue('conditions', {
        combinator: 'and',
        rules: [createSupportPolicyDefaultRule()],
      });
    }
  }, [conditionsEnabled, setValue, watchedConditions]);

  const validConditionsCount = useMemo(
    () => getValidConditionsCount(watchedConditions),
    [watchedConditions],
  );

  useEffect(() => {
    if (!conditionsEnabled || validConditionsCount > 0) {
      clearErrors('conditions');
    }
  }, [clearErrors, conditionsEnabled, validConditionsCount]);

  useEffect(() => {
    reset(defaults, { keepDirtyValues: true }); // reset all form values
    setEditorKey((prev) => prev + 1); // force RichTextField remount
  }, [defaults, reset]);

  const type = watch('type');
  const severity = getSeverityForType(type);

  const submitHandler = (data: SupportPolicyFormValues) => {
    if (data.enableConditions) {
      const count = getValidConditionsCount(data.conditions);
      if (count === 0) {
        setError('conditions', {
          type: 'required',
          message: 'Add at least one condition to save this policy',
        });
        return;
      }
    }

    clearErrors('conditions');
    onSave?.(data);
  };

  if (isLoadingConditionFields) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box
      component="form"
      id={formId ?? generatedFormId}
      sx={{
        mb: 1.5,
        borderRadius: 1,
        p: 2,
        backgroundColor: (theme) => alpha(getSeverityColors(theme, severity).rail, 0.125),
        borderLeft: (theme) => `4px solid ${getSeverityColors(theme, severity).rail}`,
      }}
      onSubmit={handleSubmit(submitHandler)}
    >
      {/* Title */}
      <Controller
        name="title"
        control={control}
        rules={{ required: 'Title is required' }}
        render={({ field }) => (
          <TextField
            {...field}
            variant="outlined"
            size="small"
            placeholder="Title"
            fullWidth
            error={!!errors.title}
            helperText={errors.title?.message}
            sx={{ mb: 2 }}
            InputProps={{
              sx: { fontSize: '0.85rem' },
            }}
            InputLabelProps={{
              sx: { fontSize: '0.85rem' },
            }}
            autoFocus
          />
        )}
      />

      {/* Description */}
      <Controller
        name="description"
        control={control}
        rules={{
          required: 'Description is required',
          validate: (value) =>
            value?.replace(/<[^>]*>/g, '').trim() !== '' ? true : 'Description is required',
        }}
        render={({ field }) => {
          const onUpdate = ({ editor }: { editor: Editor }) => {
            const fieldContent = editor?.getHTML() || '';
            if (field.value !== fieldContent) {
              field.onChange(fieldContent);
            }
          };

          return (
            <FormControl fullWidth error={!!errors.description} sx={{ mb: 2 }}>
              <RichTextField
                key={`description-${editorKey}`}
                error={errors.description?.message}
                placeholder="Description"
                sx={{ fontSize: '0.85rem' }}
                onBlur={(_, editor) => {
                  field.onBlur();

                  const fieldContent = editor?.getHTML() || '';
                  // always update, even if empty
                  field.onChange(fieldContent);
                }}
                editorOptions={{
                  editable: true,
                  content: field.value ?? '',
                  onUpdate,
                }}
                editorContentProps={{
                  innerRef: field.ref,
                }}
              />
              {errors.description?.message ? (
                <FormHelperText sx={{ marginTop: '-2rem' }}>
                  {errors.description?.message}
                </FormHelperText>
              ) : null}
            </FormControl>
          );
        }}
      />

      {/* Type & Category */}
      <Box display="flex" gap={1} mb={2}>
        <Controller
          name="type"
          control={control}
          rules={{ required: 'Type is required' }}
          render={({ field }) => (
            <Autocomplete
              {...field}
              options={POLICY_TYPE_OPTIONS}
              value={field.value || null}
              onChange={(_, newValue) => field.onChange(newValue ?? '')}
              getOptionLabel={(opt) => TYPE_FRIENDLY_NAME_POLICIES_MAP[opt] ?? opt}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Type"
                  size="small"
                  error={!!errors.type}
                  helperText={errors.type?.message}
                  InputProps={{
                    ...params.InputProps,
                    sx: { fontSize: '0.85rem' },
                  }}
                  InputLabelProps={{
                    sx: { fontSize: '0.85rem' },
                  }}
                />
              )}
              sx={{ flex: 1 }}
            />
          )}
        />

        <Controller
          name="category"
          control={control}
          rules={{ required: 'Category is required' }}
          render={({ field }) => (
            <Autocomplete
              {...field}
              options={POLICY_CATEGORIES_STANDARD as unknown as string[]}
              value={field.value || null}
              onChange={(_, newValue) => field.onChange(newValue ?? '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  size="small"
                  error={!!errors.category}
                  helperText={errors.category?.message}
                  InputProps={{
                    ...params.InputProps,
                    sx: { fontSize: '0.85rem' },
                  }}
                  InputLabelProps={{
                    sx: { fontSize: '0.85rem' },
                  }}
                />
              )}
              sx={{ flex: 1 }}
            />
          )}
        />
      </Box>

      {/* User Groups Multi-select */}
      <Controller
        name="userGroups"
        control={control}
        render={({ field }) => (
          <Autocomplete
            {...field}
            multiple
            options={userGroupOptions.map((g) => g.id)}
            value={field.value ?? []}
            onChange={(_, newValue) => field.onChange(newValue)}
            getOptionLabel={(id) => {
              const group = userGroupOptions.find((g) => g.id === id);
              return group?.label ?? id;
            }}
            renderTags={(selected, getTagProps) =>
              selected.map((id, index) => {
                const group = userGroupOptions.find((g) => g.id === id);
                return (
                  <Chip
                    {...getTagProps({ index })}
                    key={id}
                    label={group?.label ?? id}
                    size="small"
                    sx={{ fontSize: '0.85rem' }}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Available To"
                size="small"
                sx={{ mb: 2 }}
                InputProps={{
                  ...params.InputProps,
                  sx: { fontSize: '0.85rem' },
                }}
                InputLabelProps={{
                  sx: { fontSize: '0.85rem' },
                }}
              />
            )}
          />
        )}
      />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Filters
      </Typography>

      <Box display="flex" alignItems="center" gap={1} mb={conditionsEnabled ? 0 : 1}>
        <Controller
          name="enableConditions"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={!!field.value} size="small" />}
              label="Filter by conditions"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
            />
          )}
        />

        {conditionsEnabled ? (
          <Typography variant="caption" color="text.secondary">
            {validConditionsCount
              ? `${validConditionsCount} condition${validConditionsCount === 1 ? '' : 's'}`
              : 'No conditions yet'}
          </Typography>
        ) : null}
      </Box>

      {conditionsEnabled ? (
        <Controller
          name="conditions"
          control={control}
          render={({ field }) => (
            <ConditionsContainer sx={{ mb: errors.conditions?.message ? 1 : 2 }}>
              <QueryBuilderDnD>
                <QueryBuilderMaterial muiComponents={muiComponents}>
                  <QueryBuilder
                    getDefaultField={(items) =>
                      (conditionFields[0] ??
                        items?.[0] ??
                        SUPPORT_POLICY_CONDITION_FIELDS[0]) as unknown as string
                    }
                    enableMountQueryChange={false}
                    autoSelectField
                    resetOnFieldChange
                    resetOnOperatorChange={false}
                    context={queryBuilderContext}
                    showCombinatorsBetweenRules
                    getOperators={(field) =>
                      // @ts-expect-error lib incorrectly types this prop
                      (field as (typeof SUPPORT_POLICY_CONDITION_FIELDS)[number])?.operators ??
                      SUPPORT_POLICY_CONDITION_OPERATORS
                    }
                    query={mappedConditions as unknown as RuleGroupType}
                    onQueryChange={(value: unknown) =>
                      field.onChange(value as SupportPolicyRuleGroup)
                    }
                    fields={conditionFields as unknown as FullField[]}
                    getDefaultRule={() => ({
                      ...createSupportPolicyDefaultRule(),
                      field: conditionFields[0] ?? SUPPORT_POLICY_CONDITION_FIELDS[0],
                    })}
                    // @ts-expect-error lib incorrectly types this prop
                    controlElements={conditionsCustomControls}
                  />
                </QueryBuilderMaterial>
              </QueryBuilderDnD>
              {errors.conditions?.message ? (
                <FormHelperText error sx={{ mt: 1 }}>
                  {errors.conditions.message}
                </FormHelperText>
              ) : null}
            </ConditionsContainer>
          )}
        />
      ) : null}

      {/* Valid Until */}
      <Controller
        name="validUntil"
        control={control}
        render={({ field }) => (
          <DatePicker
            {...field}
            label="Valid Until"
            value={field.value}
            onChange={(newValue) => field.onChange(newValue)}
            minDate={dayjs().add(1, 'day')}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                variant: 'outlined',
                error: !!errors.validUntil,
                helperText: errors.validUntil?.message,
                sx: { mb: 2 },
                InputProps: {
                  sx: { fontSize: '0.85rem' },
                },
                InputLabelProps: {
                  sx: { fontSize: '0.85rem' },
                },
              },
            }}
          />
        )}
      />

      {/* Pin checkbox */}
      <Controller
        name="pinnedAt"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Checkbox {...field} checked={!!field.value} size="small" />}
            label="Pin this policy"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
          />
        )}
      />
    </Box>
  );
};
